import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePrintView } from '../store/usePrintView';
import { useUser } from '../store/useUser';
import {
  Search,
  Home,
  Printer,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MenuBar } from "./menuBar";

type HeaderBreadcrumb = {
  label: string;
  path?: string;
};

type HeaderSettings = {
  breadcrumbs?: HeaderBreadcrumb[];
  withoutSearch?: boolean;
  withPrint?: boolean;
};

const HOME_BREADCRUMB: HeaderBreadcrumb = { label: 'Diversion Database', path: '/dashboard' };

export const Header = ({ settings }: { settings?: HeaderSettings }) => {
  const { user, isUserLoading } = useUser();
  const { isPrintView } = usePrintView();
  const navigate = useNavigate();
  const imagePath = process.env.PUBLIC_URL;
  const dhsUrl = 'https://dhs.lacounty.gov/';
  const laCountyUrl = 'https://lacounty.gov/';
  const rawBreadcrumbs = settings?.breadcrumbs;
  const pageBreadcrumbs = Array.isArray(rawBreadcrumbs) ? rawBreadcrumbs.filter((item) => item?.label) : [];
  const breadcrumbs = pageBreadcrumbs.length > 0 ? [HOME_BREADCRUMB, ...pageBreadcrumbs] : [];
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedQuery = searchQuery.trim();
    setSearchQuery('');

    if (!trimmedQuery) {
      navigate('/search');
      return;
    }

    navigate('/search', { state: { searchQuery: trimmedQuery } });
  };
  
  if (isPrintView) return null;

  return (
    <header className="z-20 mb-2 w-full flex-shrink-0 px-0 pt-1 sm:px-1 lg:px-1 xl:px-2">
      <div className="app-header-shell">
        <div className="app-header-brand-rail">
          <div className="flex min-w-0 items-center gap-2.5">
            <a href={dhsUrl} target="_blank" rel="noreferrer" className="app-header-brand-logos">
              <img
                src={`${imagePath}/dhs-logo.svg`}
                alt="Los Angeles County Department of Health Services"
                className="h-6 w-auto"
              />
            </a>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="app-header-user-meta min-w-0 transition-colors hover:text-slate-900"
            >
              Diversion Database
            </button>
          </div>
          <div className="flex items-center gap-2.5">
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.location.href = '/menu.php'}
              className="h-7 gap-1.5 px-2 text-xs bg-green-500 text-white hover:bg-green-600 border-green-600 transition-colors"
            >
              <Home className="h-3.5 w-3.5" />
              Return to Classic DD Dashboard
            </Button>
            <div className="app-header-user-meta hidden md:block">
              {isUserLoading ? 'Loading...' : user?.realname || 'Not logged in'}
            </div>
            <a
              href={laCountyUrl}
              target="_blank"
              rel="noreferrer"
              className="opacity-75 transition hover:opacity-100"
            >
              <img
                src={`${imagePath}/lacountyseal.png`}
                alt="County of Los Angeles seal"
                className="h-5 w-5 object-contain"
              />
            </a>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-1.5 sm:px-4">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <div
              className="flex min-w-0 cursor-pointer items-center gap-2.5"
              onClick={() => navigate('/dashboard')}
              aria-label="Diversion Database home"
            >
              <img
                src={`${imagePath}/logoODR.png`}
                alt="Office of Diversion and Reentry"
                className="h-8 w-auto object-contain"
                style={{ transform: 'scale(1.12)', transformOrigin: 'left center' }}
              />
            </div>

            <div className="ml-1">
              <MenuBar />
            </div>
          </div>

          <div className="flex w-full min-w-0 flex-wrap items-center justify-end gap-2 sm:w-auto">
            {settings?.withPrint ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.print()}
                className="gap-2 text-gray-500 hover:text-[var(--azure-primary)]"
              >
                <Printer className="h-4 w-4" />
                <span className="hidden md:inline">Print</span>
              </Button>
            ) : null}

            {!settings?.withoutSearch ? (
              <form
                onSubmit={handleSearchSubmit}
                autoComplete="off"
                className="flex w-full min-w-0 items-center justify-end gap-2 sm:w-auto"
              >
                <div className="min-w-0 flex-1 sm:w-[204px] lg:w-[255px]">
                  <Input
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Search participants"
                    className="h-8 w-full"
                  />
                </div>
                <Button type="submit" size="sm" className="gap-2">
                  <Search className="h-4 w-4" />
                  <span className="hidden lg:inline">Search</span>
                </Button>
              </form>
            ) : null}
          </div>
        </div>
      </div>

      {breadcrumbs.length > 0 ? (
        <div className="px-1 pt-1 sm:px-2">
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
            {breadcrumbs.map((item, index) => {
              const isLast = index === breadcrumbs.length - 1;

              return (
                <React.Fragment key={`${item.label}-${index}`}>
                  {index > 0 ? <ChevronRight className="h-3.5 w-3.5 text-slate-400" /> : null}
                  {item.path && !isLast ? (
                    <button
                      type="button"
                      className="transition-colors hover:text-slate-900"
                      onClick={() => navigate(item.path || '/dashboard')}
                    >
                      {item.label}
                    </button>
                  ) : null}
                  {!item.path || isLast ? (
                    <span className={isLast ? 'font-medium text-slate-900' : ''}>{item.label}</span>
                  ) : null}
                </React.Fragment>
              );
            })}
          </nav>
        </div>
      ) : null}
    </header>
  );
};
